import { api } from './axios';

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы с событиями (календарь)
 */
export const eventsApi = {
    /**
     * Получить список событий с пагинацией и фильтрами
     * @param {Object} params - Параметры (page, size, sortBy, direction, status, type, search)
     * @returns {Promise<Object>}
     */
    list: async (params = {}) => {
        try {
            const { data } = await api.get('/events', { params });
            return data;
        } catch (error) {
            console.error('[eventsApi.list] Failed to fetch events:', error.message);
            throw error;
        }
    },

    /**
     * Получить событие по ID
     * @param {string|number} id - ID события
     * @returns {Promise<Object>}
     */
    get: async (id) => {
        validateId(id, 'Event ID');
        try {
            const { data } = await api.get(`/events/${id}`);
            return data;
        } catch (error) {
            console.error(`[eventsApi.get] Failed to fetch event ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать новое событие
     * @param {Object} payload - Данные события
     * @returns {Promise<Object>}
     */
    create: async (payload) => {
        if (!payload) throw new Error('Event payload is required');
        try {
            const { data } = await api.post('/events', payload);
            return data;
        } catch (error) {
            console.error('[eventsApi.create] Failed to create event:', error.message);
            throw error;
        }
    },

    /**
     * Обновить событие
     * @param {string|number} id - ID события
     * @param {Object} payload - Данные события
     * @returns {Promise<Object>}
     */
    update: async (id, payload) => {
        validateId(id, 'Event ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/events/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[eventsApi.update] Failed to update event ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить событие
     * @param {string|number} id - ID события
     * @returns {Promise<Object>}
     */
    remove: async (id) => {
        validateId(id, 'Event ID');
        try {
            const { data } = await api.delete(`/events/${id}`);
            return data;
        } catch (error) {
            console.error(`[eventsApi.remove] Failed to delete event ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить предстоящие события
     * @returns {Promise<Array>}
     */
    getUpcoming: async () => {
        try {
            const { data } = await api.get('/events/upcoming');
            return data;
        } catch (error) {
            console.error('[eventsApi.getUpcoming] Failed to fetch upcoming events:', error.message);
            throw error;
        }
    },

    /**
     * Получить события текущего пользователя
     * @returns {Promise<Array>}
     */
    getMyEvents: async () => {
        try {
            const { data } = await api.get('/events/my');
            return data;
        } catch (error) {
            console.error('[eventsApi.getMyEvents] Failed to fetch my events:', error.message);
            throw error;
        }
    },

    /**
     * Ответить на приглашение на событие
     * @param {string|number} id - ID события
     * @param {string} status - GOING | MAYBE | DECLINED
     * @returns {Promise<Object>}
     */
    respond: async (id, status) => {
        validateId(id, 'Event ID');
        if (!status) throw new Error('Status is required');
        try {
            const { data } = await api.post(`/events/${id}/respond`, { status });
            return data;
        } catch (error) {
            console.error(`[eventsApi.respond] Failed to respond to event ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить список участников события
     * @param {string|number} id - ID события
     * @returns {Promise<Array>}
     */
    getParticipants: async (id) => {
        validateId(id, 'Event ID');
        try {
            const { data } = await api.get(`/events/${id}/participants`);
            return data;
        } catch (error) {
            console.error(`[eventsApi.getParticipants] Failed to fetch participants for event ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать напоминание для события
     * @param {string|number} id - ID события
     * @param {Object} payload - { reminderTime, channel: IN_APP | EMAIL | PUSH }
     * @returns {Promise<Object>}
     */
    createReminder: async (id, payload) => {
        validateId(id, 'Event ID');
        if (!payload) throw new Error('Reminder payload is required');
        try {
            const { data } = await api.post(`/events/${id}/reminders`, payload);
            return data;
        } catch (error) {
            console.error(`[eventsApi.createReminder] Failed to create reminder for event ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить список напоминаний события
     * @param {string|number} id - ID события
     * @returns {Promise<Array>}
     */
    getReminders: async (id) => {
        validateId(id, 'Event ID');
        try {
            const { data } = await api.get(`/events/${id}/reminders`);
            return data;
        } catch (error) {
            console.error(`[eventsApi.getReminders] Failed to fetch reminders for event ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить напоминание
     * @param {string|number} id - ID события
     * @param {string|number} reminderId - ID напоминания
     * @returns {Promise<Object>}
     */
    removeReminder: async (id, reminderId) => {
        validateId(id, 'Event ID');
        validateId(reminderId, 'Reminder ID');
        try {
            const { data } = await api.delete(`/events/${id}/reminders/${reminderId}`);
            return data;
        } catch (error) {
            console.error(`[eventsApi.removeReminder] Failed to delete reminder ${reminderId}:`, error.message);
            throw error;
        }
    }
};
